// Login v4 - 50音タブ + 名前リスト + bottom sheet PIN
import { useEffect, useState, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
// SPEC-LOGIN-BOUNCE-AFTER-SUCCESS-01 (D-838): ログインの足跡(個人情報/トークン/PIN は入れない)
import { traceAuth, flushAuthTrace, TRACE } from '../lib/authTrace'
import { DFX_ORG_ID } from '../lib/auth/orgConstants'
import { useToast } from '../hooks/useToast'
import { fetchDeviceLoginRows, upsertLoginHistory } from '../services/loginHistory'
import TabBar from './login/TabBar'
import StaffList from './login/StaffList'
import PinSheet from './login/PinSheet'
import { startPrefetch } from '../lib/prefetchCache'
import { warmupVerifyPin } from './login/pinVerifier'
import VersionPanel from './login/VersionPanel'
import { setSentryUser, Sentry } from '../lib/sentry' // D-461 ITEM_2: ログイン成功時に staff_id を Sentry に載せる
// SPEC-LOGIN-GHOST-SESSION-TRANSIENT-FIX-V1 (D-733): 時間切れの値は AuthProvider と同じものを **輸入する**。
//   ★ここに 6000 と書き直すと、片方だけ変わってずれる(D-616)。
import { AUTH_INIT_TIMEOUT_MS } from '../lib/auth/AuthProvider'
// SPEC-LOGIN-BOUNCE-AFTER-SUCCESS-01 (D-838) AC2 FIX_A: 新しいセッションの開始で離席時刻を今にする。
//   ★キーの文字列はここに書き写さない(1 か所定義 / D-616)。
import { writeSessionActivity } from '../lib/auth/sessionActivity'
// D-993b: トップの Face ID(この端末で ON の人だけ)。1 人なら開いたときに自動で 1 回だけ始める。
import { listFaceIdEnabledStaffIds, loginWithPasskey, preparePasskeyLogin } from '../lib/webauthn'
import { claimFaceIdAutoStart, clearManualLogout } from '../lib/auth/faceIdAutoStart'

const KANA_GROUPS = {
  'あ': /^[アイウエオ]/,
  'か': /^[カキクケコガギグゲゴ]/,
  'さ': /^[サシスセソザジズゼゾ]/,
  'た': /^[タチツテトダヂヅデド]/,
  'な': /^[ナニヌネノ]/,
  'は': /^[ハヒフヘホバビブベボパピプペポ]/,
  'ま': /^[マミムメモ]/,
  'や': /^[ヤユヨ]/,
  'ら': /^[ラリルレロ]/,
  'わ': /^[ワヰヱヲン]/,
}

// ひらがな → カタカナ変換（name_kana がひらがな保存の場合に対応）
function toKatakana(str) {
  return (str || '').replace(/[ぁ-ゖ]/g, c =>
    String.fromCharCode(c.charCodeAt(0) + 0x60))
}

// ─── 亡霊セッションの判定 ─────────────────────────────────────────────
// SPEC-LOGIN-GHOST-SESSION-TRANSIENT-FIX-V1 (D-733)
//
// ★何が起きていたか: getUser() が失敗したら **理由を問わず** 亡霊とみなして signOut していた。
//   通信の一瞬の不調・時間切れ・5xx でもログイン画面から蹴り出される。
//   ★実測(2026-09-13): ひろは login_success 430 / login_failed 2。
//     つまり「弾かれる」のはサーバの PIN 拒否ではなく、auth_logs に痕跡が残らないこの経路。
//   ★「今すぐ更新」ボタンはログイン画面にあり、更新 → reload の戻り先もログイン画面。
//     不具合の経路と更新の経路がここで交差していた。
//
// ★世界の定石(WorkOS AuthKit / Session resilience): セッション検証の失敗は
//   **一過性(transient)** と **終局的(terminal)** を必ず分け、transient では破棄しない。
//     400 invalid_grant → terminal / 408・接続エラー・429・5xx → transient
//   「タイムアウトや 5xx を terminal 扱いすると、瞬の不調が不要なサインアウトに化ける」
//
// ★社内にも既にある。D-268b FIX_C8 が AuthProvider.jsx に同じことを書いている:
//   「時間切れ/例外/初期化時の null は『不明』であって『無し』ではないため purge しない」
//   ★新しい考え方ではない。**既に決まっている規則をもう 1 箇所に適用するだけ**。
//
// ★逆向きの屍も見ている(codecarbon #1208): 通信失敗を一律「検証成功」に倒すと
//   失効済みトークンが通り続ける。★だからここは「成功とみなす」のではなく
//   **判定を保留して既存のセッションのまま通す**。サーバ側の RLS が最終の関所として残るので、
//   通しても権限は漏れない。本当に無効なら次の読み取りで AuthProvider の再検証が確定無効として拾う。

/** サーバが「この利用者は居ない」と明示した status。★ここだけが signOut の理由になる。 */
export const GHOST_STATUSES = [401, 403]

/**
 * getUser() の結果を 3 つに分ける。
 * @returns 'ghost'   … サーバが明示的に否定した(401 / 403、または error 無しで user が null)
 *          'unknown' … 通信エラー / 例外 / 時間切れ / 408 / 429 / 5xx / status が取れないエラー
 *          'valid'   … user が返った
 * ★どちらとも言えないものは 'unknown'。**迷ったら ghost に倒さない**。
 */
export function classifyGhostSession(error, user) {
  if (error) {
    const status = error?.status
    return GHOST_STATUSES.includes(status) ? 'ghost' : 'unknown'
  }
  return user ? 'valid' : 'ghost'
}

/** 時間切れの印。★error として classifyGhostSession に渡すと status が無いので 'unknown'。 */
export function makeGetUserTimeoutError(ms) {
  const e = new Error(`getUser timeout ${ms}ms`)
  e.name = 'AuthGetUserTimeout'
  return e
}

/**
 * getUser() に時間切れを付けて呼ぶ。★時間切れは失敗ではなく **不明**。
 * @returns {Promise<{error: unknown, user: unknown}>}
 */
export async function getUserWithTimeout(client, ms = AUTH_INIT_TIMEOUT_MS) {
  let timer
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve({ error: makeGetUserTimeoutError(ms), user: null }), ms)
  })
  const call = Promise.resolve()
    .then(() => client.auth.getUser())
    .then(({ data, error }) => ({ error: error ?? null, user: data?.user ?? null }))
    .catch((e) => ({ error: e ?? new Error('getUser threw'), user: null }))
  try {
    return await Promise.race([call, timeout])
  } finally {
    clearTimeout(timer)
  }
}

/**
 * 'unknown' を見えるようにする。★console.warn だけにしない。
 * ★Sentry の DSN が本番に無い間はどこにも出ない(それを承知で入れる / DSN 復旧は別件)。
 * ★トークンや個人情報は絶対に送らない(AuthProvider の reportAuthInit と同じ形)。
 */
export function reportGhostUnknown(error) {
  try {
    Sentry.captureMessage('login ghost check unknown', {
      level: 'warning',
      tags: { diag: 'D-733', kind: 'unknown' },
      extra: { name: error?.name ?? null, status: error?.status ?? null },
    })
  } catch { /* 計測失敗でログインを止めない */ }
}

export default function Login() {
  const navigate = useNavigate()
  const { showToast, Toast } = useToast({ successDuration: 1200 })

  const [allStaff, setAllStaff]         = useState([])
  const [starStaff, setStarStaff]       = useState([])
  const [activeTab, setActiveTab]       = useState('★')
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [initDone, setInitDone]         = useState(false)
  const [loadErr, setLoadErr]           = useState('')
  // SPEC-VERSION-VISIBILITY-AND-UPDATE-V1 (D-456) ITEM_6: 強制更新中はログインさせない(更新のみ提示)。
  //   強制はログイン画面限定=ここだけ。ログイン後の作業中には一切かけない(入力が飛ぶため)。
  const [forcedUpdate, setForcedUpdate] = useState(false)
  // D-993b gate_3 #1: 版の確認(VersionPanel の version.json / app_config)の結果が出たか。
  //   ★自動の Face ID はこれが立つまで始めない(強制更新の判定より先にログインさせない)。
  //   ★VersionPanel は取得失敗を「強制しない」= false で必ず 1 回報告する(PIN と同じ扱い)。
  //   forcedRef = 非同期の .then の中で最新の強制判定を読むため(state は古い描画の値のまま)。
  const [versionChecked, setVersionChecked] = useState(false)
  const forcedRef = useRef(false)
  const handleForcedChange = (forced) => {
    forcedRef.current = forced
    setForcedUpdate(forced)
    setVersionChecked(true)
  }
  // D-993b: トップの Face ID。
  //   quickBusy = タップで実行中の staff_id(タップの二重押しを止める)。
  //   autoPending = 開いた時点の自動の試みの staff_id(★ボタンは押せるまま: 自動の get() が返ってこない端末でも
  //   タップで始め直せる。@simplewebauthn/browser は新しい呼び出しで前の get() を AbortError で取り消す)。
  const [quickBusy, setQuickBusy]       = useState(null)
  const [autoPending, setAutoPending]   = useState(null)
  const [quickError, setQuickError]     = useState(false)
  const preparedRef  = useRef({})     // staff_id → 先に取った challenge(タップから get() までに通信を挟まない)
  // D-993b gate_3 #5: 試みの世代。タップで 1 つ進める → begin 待ちの自動の試みは get() を呼ばずに降りる
  //   (後から自動の get() を呼ぶと、タップの get() が AbortError で取り消されてしまう)。
  const attemptRef   = useRef(0)

  // SPEC-LOGIN-VERIFYPIN-WARMUP-IMPL-01 R2(A): mount時にverify-pin Edgeを事前warm-up
  useEffect(() => { warmupVerifyPin() }, [])

  // SPEC-PWA-SW-UPDATE-REDUCTION-01 (D-112): 層D撤去。/login マウント時の version.json 比較→reload (D-062 保険層)
  // を断つ。SW更新検知は swRegistration の statechange 購読 + バナー明示タップ適用に一本化 (ログイン中フラッシュ排除)。
  // versionReload.js 自体は defaultWaitForController を preloadReloadGuard が再利用するため温存 (呼び出し元のみ撤去)。

  useEffect(() => {
    async function init() {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        // SPEC-LOGIN-GHOST-SESSION-VALIDATE-FIX-01: getUser() でサーバー側の生存確認。
        // 削除済み auth.user の亡霊 session は無音破棄して staff 一覧へフォールスルー。
        // ★D-733: 失敗の **理由** で分ける。通信の一時的な不調で蹴り出さない。
        const { error: userError, user } = await getUserWithTimeout(supabase)
        const verdict = classifyGhostSession(userError, user)
        if (verdict === 'ghost') {
          // ★サーバが明示的に否定したときだけ。ここだけが signOut の理由。
          console.warn('[ERR-LOGIN-GHOST-SESSION]', userError)
          try { await supabase.auth.signOut() } catch (e) { console.warn('[ERR-LOGIN-GHOST-SESSION] signOut', e) }
          // fall through: return せず staff 一覧ロードへ続行
        } else {
          // ★'unknown' も通す。signOut しない(セッションを壊さない)。
          //   通しても権限は漏れない = サーバ側の RLS が最終の関所。
          if (verdict === 'unknown') {
            console.warn('[WARN-LOGIN-GHOST-UNKNOWN]', userError)
            reportGhostUnknown(userError)
          }
          navigate('/launcher', { replace: true })
          return
        }
      }
      // SPEC-LOGIN-LATENCY-FIX-01 C2: staff SELECT と device_login_history SELECT を
      // Promise.all で並列発火 (waterfall +737ms cold 解消)。
      // organization_id フィルタは外す: anon RLS が is_active=true のみ対象のため、
      // org_id で絞ると環境によっては空配列になる。
      const [staffRes, deviceRows] = await Promise.all([
        // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
        supabase
          .from('staff')
          .select('staff_id, name, name_kana, has_pin')
          .eq('is_active', true)
          .order('name_kana'),
        fetchDeviceLoginRows(),
      ])

      const { data, error } = staffRes
      if (error) {
        setLoadErr(`スタッフ一覧の取得に失敗しました (${error.message})`)
        setInitDone(true)
        return
      }

      let staff = data || []

      // staff テーブルが空の場合は staff_public ビューにフォールバック
      if (staff.length === 0) {
        // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
        const { data: pub } = await supabase
          .from('staff')
          .select('staff_id, name, name_kana, has_pin')
          .eq('is_active', true)
          .order('name')
        if (pub?.length) staff = pub
      }

      setAllStaff(staff)
      // device_login_history rows を staff list にマッピング (旧 fetchStarStaff の後半)
      const staffMap = Object.fromEntries(staff.map(s => [s.staff_id, s]))
      const seenStarIds = new Set()
      const star = (deviceRows || [])
        .map(h => staffMap[h.staff_id])
        .filter(s => s && !seenStarIds.has(s.staff_id) && seenStarIds.add(s.staff_id))
      setStarStaff(star)
      setInitDone(true)
    }
    init()
  }, [navigate])

  // SPEC-LOGIN-AUTH-LATENCY-PHASE1-01 R3: 楽観遷移失敗時のエラートースト表示
  useEffect(() => {
    if (!initDone) return
    const authError = sessionStorage.getItem('loginAuthError')
    if (authError) {
      sessionStorage.removeItem('loginAuthError')
      showToast(authError, 'error')
    }
  }, [initDone])

  const starStaffIds = useMemo(
    () => new Set(starStaff.map(s => s.staff_id)),
    [starStaff]
  )

  const filteredStaff = useMemo(() => {
    if (activeTab === '★') return starStaff
    const regex = KANA_GROUPS[activeTab]
    if (!regex) return allStaff
    return allStaff.filter(s => regex.test(toKatakana(s.name_kana || '')))
  }, [activeTab, allStaff, starStaff])

  // D-993b: この端末で Face ID が ON の人(在籍一覧にいる人だけ。外れた人の古い印は無視)。
  const faceIdStaff = useMemo(() => {
    const ids = new Set(listFaceIdEnabledStaffIds())
    return ids.size ? allStaff.filter(s => ids.has(s.staff_id)) : []
  }, [allStaff])

  // method: 'pin'(既定。PinSheet の PIN)/ 'passkey'(Face ID)。足跡の名前だけを分ける(D-993b gate_3 #10)。
  const handleLoginSuccess = async (staff, session, method = 'pin') => {
    // D-993b: どの入り方でも、入れたら「明示ログアウト直後」の印を外す(次に開いたときは自動で Face ID)。
    clearManualLogout()
    // SPEC-LOGIN-BOUNCE-AFTER-SUCCESS-01 (D-838): 照合は成功している(ここに来た時点で 200)。
    traceAuth(TRACE.verifyOk, method === 'passkey' ? 'passkey_verified' : 'pin_verified', { hasSession: !!session })
    // SPEC-LOGIN-BOUNCE-AFTER-SUCCESS-01 (D-838) AC2 FIX_A: ★これから始まるセッションの「最後の操作」は今である。
    //   ここで印を付けないと、前のセッションが残した古い時刻(明示ログアウト等で消えずに残ったもの)を
    //   useIdleLogout の起動時評価が読み、isLoggedIn が false→true になった瞬間に
    //   「30 分以上放置されている」と誤判定して即ログアウト → /login へ押し戻す。
    //   ★2026-09-22 の auth_trace で二重ログイン 3 件が 3 件ともこの形だった(launcher_reached 0 件)。
    //   ★setSession の **前** に書く: SIGNED_IN で isLoggedIn が立つ前に印を済ませるため。
    //   ★D-360 の門番(PWA 破棄後の新規ロードで 30 分を測る)は温存する。印を付けるのは
    //     「今この端末で PIN を通した」ときだけで、復元されたセッションには何もしない。
    writeSessionActivity(Date.now())
    const { error: sessionError } = await supabase.auth.setSession({
      access_token:  session.access_token,
      refresh_token: session.refresh_token,
    })
    // D-299 IMPL_1(方針A): prefetch は setSession 完了後(=authenticated)に発火する。
    //   従来は StaffList onSelect(=PIN検証前=anon)で走り、staff_stores が RLS で常に 0 行になり
    //   現場ロールの未対応報告が空キャッシュで 0 件表示になっていた(本番バグ)。await しない=遷移をブロックしない。
    //   setSession が失敗したときは呼ばない(anon で走らせない)。
    // ★D-838: setSession が失敗しても、この先は今のところ /launcher へ進んでしまう。
    //   そこで押し戻されるなら、それが原因。まず記録して実データで確かめる(まだ挙動は変えない)。
    traceAuth(TRACE.setSession, sessionError ? 'set_session_error' : 'set_session_ok',
      { sessionError: !!sessionError })
    if (!sessionError) startPrefetch(staff.staff_id)
    // D-461 ITEM_2: setSession 成功後に Sentry へ利用者(staff_id のみ)を載せる。遷移はブロックしない。
    if (!sessionError) setSentryUser(staff.staff_id)
    // SPEC-LOGIN-SUCCESS-UNBLOCK-01 R1: non-blocking (do not await)
    upsertLoginHistory(staff.staff_id).catch(e => console.warn('[ERR-LOGIN-HISTORY]', e))
    setSelectedStaff(null)
    showToast(`${staff.name} さん こんにちは`)
    // ★認証済みになった今だけ、貯めておいた足跡をまとめて送る(未ログインからは書かせない)。
    if (!sessionError) flushAuthTrace(supabase).catch(() => {})
    navigate('/launcher', { replace: true })
  }

  const prefetchFaceId = (s) => {
    preparePasskeyLogin(s).then(p => { if (p) preparedRef.current[s.staff_id] = p })
  }

  // D-993b: 開いたときの自動 Face ID。★1 人のときだけ・ページ読み込みにつき 1 回だけ(claimFaceIdAutoStart)。
  //   ★明示ログアウト直後は始めない(押した直後に入り直してしまうため)。ボタンは出る。
  //   ★失敗・キャンセル・操作なしで拒まれた(NotAllowedError / SecurityError)は **黙って** ボタンに任せる。
  //   ★iOS Safari が操作なしで許す get() はページごとに 1 回 = これがこのページ最初の get() であること。
  //   ★gate_3 #1: 版の確認の結果(versionChecked)が出るまで待つ。結果が出た後でも、成功時に強制更新なら入れない。
  //   ★gate_3 #5: begin(challenge)を先に済ませ、その間にタップされていたら get() を呼ばずに降りる(タップが勝つ)。
  useEffect(() => {
    if (!initDone || !versionChecked || forcedUpdate || loadErr || faceIdStaff.length === 0) return
    // gate_3 #1 の続き: 版の確認を待つ間にタップ済みなら、自動は始めない(後から get() を呼ぶとタップの get() を取り消す)。
    //   ★claim は使わない(この effect は一覧が揃った後に再び走らないので、どちらでも同じ)。
    if (attemptRef.current !== 0) return
    if (faceIdStaff.length === 1 && claimFaceIdAutoStart()) {
      const s = faceIdStaff[0]
      const gen = ++attemptRef.current
      const superseded = () => attemptRef.current !== gen
      setAutoPending(s.staff_id)
      preparePasskeyLogin(s).then(prepared => {
        // タップが始まった / 強制更新が分かった → 黙って降りる(タップ側が画面の状態を持つ)
        if (superseded() || forcedRef.current) return null
        if (!prepared) return { ok: false }
        return loginWithPasskey(s, prepared)
      }).then(result => {
        if (superseded()) return
        setAutoPending(null)
        if (!result || forcedRef.current) return // 強制更新中は入れない(PIN シートも出ない状態)
        if (result.ok) { handleLoginSuccess(s, result.session, 'passkey'); return }
        prefetchFaceId(s)
      })
      return
    }
    faceIdStaff.forEach(prefetchFaceId)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 一覧が揃った時点で 1 回だけ判定する(handleLoginSuccess は毎描画で作り直される)
  }, [initDone, versionChecked, forcedUpdate, loadErr, faceIdStaff])

  // D-993b: トップのボタン。失敗は PinSheet と同じ文言で PIN へ誘導(下の一覧から PIN で入れる)。
  const tapFaceId = async (s) => {
    if (quickBusy) return
    attemptRef.current += 1 // gate_3 #5: begin 待ちの自動の試みを降ろす
    setAutoPending(null)
    setQuickBusy(s.staff_id)
    setQuickError(false)
    const prepared = preparedRef.current[s.staff_id]
    delete preparedRef.current[s.staff_id] // challenge は 1 回きり
    const result = await loginWithPasskey(s, prepared)
    // gate_3 #1: 照合中に強制更新が分かったら入れない(ボタンも PIN シートも隠れている状態)
    if (forcedRef.current) { setQuickBusy(null); return }
    if (result.ok) { handleLoginSuccess(s, result.session, 'passkey'); return }
    setQuickBusy(null)
    setQuickError(true)
    prefetchFaceId(s)
  }

  if (!initDone) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0f' }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        <div style={{ width: 32, height: 32, border: '2px solid #f0c040', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    )
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#0a0a0f', color: '#e8e8f0', fontFamily: "-apple-system,BlinkMacSystemFont,'Hiragino Kaku Gothic ProN',sans-serif" }}>
      <Toast />

      {/* ヘッダー */}
      <div style={{ flexShrink: 0, padding: '16px 16px 10px', textAlign: 'center' }}>
        <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: 1 }}>Round 0</div>
        <div style={{ fontSize: 18, color: '#9090a8', marginTop: 2 }}>スタッフを選んでPINを入力</div>
        {/* SPEC-VERSION-VISIBILITY-AND-UPDATE-V1 (D-456) ITEM_4: 版表示・状態バッジ・更新ボタン(+ITEM_6 強制判定)。
            旧 D-384 の v表示のみ→現在の版/古い判定/その場更新に拡張。build#/sha は VersionPanel 内で小さく残す。 */}
        <VersionPanel onForcedChange={handleForcedChange} />
      </div>

      {forcedUpdate ? (
        /* ITEM_6: 強制更新中はスタッフ一覧/PIN を出さない(先へ進めない)。更新導線は上の VersionPanel のみ。 */
        <div style={{ flex: 1 }} />
      ) : (
      <>
      {/* D-993b: この端末で Face ID が ON の人は、名前を選ばずに 1 タップ(1 人なら開いた時点で自動)。 */}
      {!loadErr && faceIdStaff.length > 0 && (
        <div data-testid="faceid-quick-login" style={{ flexShrink: 0, padding: '0 16px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {faceIdStaff.map(s => (
            <button
              key={s.staff_id}
              type="button"
              data-testid={`faceid-quick-${s.staff_id}`}
              onClick={() => tapFaceId(s)}
              disabled={!!quickBusy}
              aria-busy={(quickBusy || autoPending) === s.staff_id}
              style={{
                width: '100%', minHeight: 52, borderRadius: 12,
                background: '#06b6d4', color: '#0f172a', border: 'none',
                fontSize: 18, fontWeight: 700, cursor: quickBusy ? 'default' : 'pointer',
                opacity: quickBusy && quickBusy !== s.staff_id ? 0.5 : 1, touchAction: 'manipulation',
              }}
            >
              {(quickBusy || autoPending) === s.staff_id ? 'Face ID で確認中…' : `${s.name} さん Face ID でログイン`}
            </button>
          ))}
          {quickError && (
            <div data-testid="faceid-quick-error" style={{ textAlign: 'center', fontSize: 14, color: '#f87171' }}>
              Face ID で確認できませんでした。PIN で入ってください
            </div>
          )}
        </div>
      )}

      {/* タブバー */}
      <TabBar active={activeTab} onChange={setActiveTab} />

      {/* 件数バッジ */}
      <div style={{ flexShrink: 0, padding: '4px 12px 0', fontSize: 15, color: '#64748b' }}>
        {filteredStaff.length}件
      </div>

      {/* 名前リスト(スクロールエリア) */}
      <div style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
        {loadErr ? (
          <div style={{ padding: '48px 16px', textAlign: 'center', color: '#f87171', fontSize: 15 }}>{loadErr}</div>
        ) : (
          <StaffList
            staff={filteredStaff}
            starStaffIds={starStaffIds}
            isStarTab={activeTab === '★'}
            onSelect={staff => {
              setSelectedStaff(staff)
              // D-299 IMPL_1: prefetch は onSelect(anon)から外し、handleLoginSuccess(setSession 後=authenticated)へ移設。
              // SPEC-LOGIN-VERIFYPIN-WARMUP-IMPL-01 R3(C): tap時にEdgeを再warm-up(cooldown内ならskip)
              warmupVerifyPin()
            }}
          />
        )}
      </div>

      {/* bottom sheet */}
      {selectedStaff && (
        <PinSheet
          staff={selectedStaff}
          onClose={() => setSelectedStaff(null)}
          onSuccess={handleLoginSuccess}
        />
      )}
      </>
      )}
    </div>
  )
}
